import notify from '@/plugins/notify';

import {
    IDraftType,
    IGameState,
    IGameStateResponse,
    IGameStatus,
    IFortResource,
} from './Game-types';
import api from '../../helpers/api';
import {
    IUnit,
    IUnitType,
    UNIT_STATE_DEPLOYED,
} from '../Unit/Unit-types';
import {
    IDecree,
} from '../DecreeCard/DecreeCard-types';
import Router from '../../router';
import { store } from '../../main';

const gameState: IGameState = {
    state: {
        activePlayerId: null,
        altFootman: false,
        altLancer: false,
        altMarshal: false,
        choosePlayOrder: false,
        creatorId: null,
        customUnitTypes: [],
        customUnits: false,
        decrees: [],
        draft: false,
        draftType: IDraftType.RANDOM,
        draftingPlayerId: null,
        facedownUnits: [],
        forts: [],
		highSeas: false,
        loaded: false,
        mastery: false,
        nobility: false,
        numOfPlayers: null,
        passwordRequired: false,
        players: [],
		queueMode: false,
        ranked: false,
        roomName: '',
        royalGuardType: false,
        nightfall: false,
        siege: false,
        state: null,
        teams: [],
        timeLimit: null,
        turnOrder: [],
        unitTypes: [],
        unitsInBag: [],
        unitsInHand: [],
        updatedAt: null,
        updatedSkillRating: null,
        winningTeamId: null,
        winningScore: 6,
        poisonTokens: [],
        twoPlayersFullBoard: false,
        theme: null,
    },
    mutations: {
        setGameState(state, game: IGameStateResponse) {
            state.activePlayerId = game.activePlayerId;
            state.choosePlayOrder = game.choosePlayOrder;
            state.creatorId = game.creator.uuid;
            state.customUnitTypes = game.customUnitTypes;
            state.customUnits = game.customUnits;
            state.draft = game.draft;
            state.draftType = game.draftType;
            state.draftingPlayerId = game.draftingPlayerId;
            state.loaded = true;
            state.numOfPlayers = game.numOfPlayers;
            state.passwordRequired = Boolean(game.passwordRequired);
            state.players = game.players;
            state.ranked = game.ranked;
            state.roomName = game.roomName;
            state.royalGuardType = game.royalGuardType;
            state.state = game.state;
            state.teams = game.teams;
            state.timeLimit = game.timeLimit;
            state.turnOrder = game.turnOrder;
            state.updatedAt = game.updatedAt;
            state.winningScore = game.winningScore;
            state.winningTeamId = game.winningTeamId;
            state.poisonTokens = game.poisonTokens;
            state.startedAt = game.startedAt;

            if (!state.siege) {
                state.forts = [];
            }

            if (!state.nobility) {
                state.decrees = [];
            }

            if (game.settings) {
                state.settingsUpdatedAt = game.settings.updatedAt;
                state.twoPlayersFullBoard = game.settings.twoPlayersFullBoard;
				state.nobility = game.settings.nobility;
				state.nightfall = game.settings.nightfall;
				state.siege = game.settings.siege;
                state.champions = game.settings.champions;
                state.highSeas = game.settings.highSeas;
                state.mastery = game.settings.mastery;
                state.shock = game.settings.shock;
                state.blitz = game.settings.blitz;
                state.altFootman = game.settings.altFootman;
                state.altLancer = game.settings.altLancer;
                state.altMarshal = game.settings.altMarshal;
                state.theme = game.settings.theme;
				state.queueMode = game.settings.queueMode;
            }
        },
        setUnitsInHand(state, units: IUnit[]) {
            state.unitsInHand = units;
        },
        setUnitsInBag(state, units: IUnit[]) {
            state.unitsInBag = units;
        },
        setFacedownUnits(state, facedownUnits: IUnit[]) {
            state.facedownUnits = facedownUnits;
        },
        setUnitTypes(state, unitTypes: IUnitType[]) {
            state.unitTypes = unitTypes;
        },
        setUpdatedSkillRating(state, updatedSkillRating: number) {
            state.updatedSkillRating = updatedSkillRating;
        },
        setGameLoaded(state, loaded: boolean) {
            state.loaded = loaded;
        },
        setDecrees(state, decrees: IDecree[]) {
            state.decrees = decrees;
        },
        setForts(state, forts: IFortResource[]) {
            state.forts = forts;
        },
    },
    actions: {
        async cancelMatch({}, gameId: number) {
            const response = await api.request(`/game/${gameId}/cancel`, 'POST');

            if (!response.ok) {
                const error = await response.json();

                notify({
                    group: 'app',
                    title: 'Error',
                    type: 'error',
                    text: error.message,
                });
            }
        },
        async getGameState({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/state`);
            const game: IGameStateResponse = await response.json();

            if (response.ok) {
                commit('setGameState', game);
            } else if (response.status === 401) {
                Router.push({ name: 'login', params: { redirect: `/game/${gameId}` } });
            } else if (response.status === 404) {
                Router.push('/rooms');
            }
        },
        async setGameState({ commit }, updatedGameState: IGameStateResponse) {
            if (updatedGameState.state === IGameStatus.CANCELLED) {
                Router.push('/rooms');
                notify({
                    group: 'app',
                    title: 'Info',
                    type: 'info',
                    text: 'The game has been cancelled',
                });
                return;
            }
            commit('setGameState', updatedGameState);
        },
        async getGameUnits({ commit }, playerId: number) {
            const promises = [
                api.request(`/player/${playerId}/hand`, 'GET'),
                api.request(`/player/${playerId}/bag`, 'GET'),
                api.request(`/player/${playerId}/facedown`, 'GET'),
            ];

            const [
                unitsInHand,
                unitsInBag,
                facedownUnits,
            ] = await Promise.all(promises);

            commit('setUnitsInHand', await unitsInHand.json());
            commit('setUnitsInBag', await unitsInBag.json());
            commit('setFacedownUnits', await facedownUnits.json());
        },
        async getUnitsInHand({ commit }, playerId: number) {
            const response = await api.request(`/player/${playerId}/hand`, 'GET');
            const units: IUnit[] = await response.json();

            if (response.ok) {
                commit('setUnitsInHand', units);
            }
        },
        async getFacedownUnits({ commit }, playerId: number) {
            const response = await api.request(`/player/${playerId}/facedown`, 'GET');

            if (response.ok) {
                const units: IUnit[] = await response.json();
                commit('setFacedownUnits', units);
            }
        },
        async getUnitsInBag({ commit }, playerId: number) {
            const response = await api.request(`/player/${playerId}/bag`, 'GET');

            if (response.ok) {
                const units: IUnit[] = await response.json();
                commit('setUnitsInBag', units);
            }
        },
        async getUnitTypes({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/unitTypes`, 'GET');

            if (response.ok) {
                const unitTypes: IUnitType[] = await response.json();
                commit('setUnitTypes', unitTypes);
            }
        },
        setUnitTypes({ commit }, unitTypes: IUnitType[]) {
            commit('setUnitTypes', unitTypes);
        },
        setUpdatedSkillRating({ commit }, updatedSkillRating: number) {
            commit('setUpdatedSkillRating', updatedSkillRating);
        },
        setGameLoaded({ commit }, loaded: boolean) {
            commit('setGameLoaded', loaded);
        },
        async leaveGame({}, gameId: number) {
            const response = await api.request('/game/leave', 'POST', { gameId });

            if (response.ok) {
                const game = await response.json();

                notify({
                    group: 'app',
                    clean: true,
                });

                if (game.state === IGameStatus.ENDED) {
                    notify({
                        group: 'app',
                        title: 'Success',
                        type: 'success',
                        text: 'You have successfully forfeited the game',
                    });
                } else {
                    if (Router.currentRoute &&
                        Router.currentRoute.name !== 'rooms') {
                        Router.push('/rooms');
                    }

                    notify({
                        group: 'app',
                        title: 'Success',
                        type: 'success',
                        text: 'You have successfully left the game',
                    });
                }
            } else {
                const error = await response.json();

                notify({
                    group: 'app',
                    title: 'Error',
                    type: 'error',
                    text: error.message,
                });
            }
        },
        async leaveAllGames() {
            return api.request('/game/leaveAll', 'POST');
        },
        async updateGameSettings({ commit }, options: any) {
            const { gameId, settings, prevSettings } = options;
            const response = await api.request(`/game/${gameId}/settings`, 'POST', settings);

            if (response.status === 403) {
                const error = await response.json();

                notify({
                    group: 'app',
                    title: 'Error',
                    type: 'warn',
                    text: error.message,
                });

                commit('restoreSettings', prevSettings);
            }
        },
        async getDecrees({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/decrees`, 'GET');

            if (response.ok) {
                const decrees: IDecree[] = await response.json();
                commit('setDecrees', decrees);
            }
        },
        async getForts({ commit }, gameId: number) {
            const response = await api.request(`/game/${gameId}/forts`, 'GET');

            if (response.ok) {
                const forts: IFortResource[] = await response.json();
                commit('setForts', forts);
            }
        },
        async getUnitsData({ commit }, playerId) {
            const response = await api.request(`/player/${playerId}/units`, 'GET');

            if (response.ok) {
                const {
                    unitsInHand,
                    unitsInBag,
                    facedownUnits,
                } = await response.json();

                commit('setUnitsInHand', unitsInHand);
                commit('setUnitsInBag', unitsInBag);
                commit('setFacedownUnits', facedownUnits);
            }
        },
        async getExpansionsData({ commit, dispatch }, gameId: number) {
            const {
                state: {
                    gameState: {
                        nobility,
                        siege,
                    },
                },
            } = store;

            if (nobility && siege) {
                const response = await api.request(`/game/${gameId}/expansions`, 'GET');

                if (response.ok) {
                    const {
                        decrees,
                        forts,
                    } = await response.json();

                    commit('setDecrees', decrees);
                    commit('setForts', forts);
                }
            } else if (nobility) {
                await dispatch('getDecrees', gameId);
            } else if (siege) {
                await dispatch('getForts', gameId);
            }
        },
        setDecrees({ commit }, decrees: IDecree[]) {
            commit('setDecrees', decrees);
        },
        setForts({ commit }, forts: IFortResource[]) {
            commit('setForts', forts);
        },
    },
    getters: {
        deployedUnitTypes: (state) => {
            const deployedUnitTypes = [];
            state.players.map((player) => {
                player.units.map((unit) => {
                    if (unit.id && unit.state === UNIT_STATE_DEPLOYED) {
                        deployedUnitTypes.push(unit.type);
                    }
                });
            });

            return deployedUnitTypes;
        },
        deployedUnitIds: (state) => {
            const deployedUnitIds = [];
            state.players.map((player) => {
                player.units.map((unit) => {
                    if (unit.id && unit.state === UNIT_STATE_DEPLOYED) {
                        deployedUnitIds.push(unit.id);
                    }
                });
            });

            return deployedUnitIds;
        },
        friendlyUnitIds: (state, getters) => {
            const friendlyUnitIds = [];
            state.players.map((player) => {
                if (player.team && player.team.id === getters.player.team.id) {
                    player.units.map((unit) => {
                        if (unit.id) {
                            friendlyUnitIds.push(unit.id);
                        }
                    });
                }
            });
            state.unitsInHand.map((unit) => {
                friendlyUnitIds.push(unit.id);
            });
            return friendlyUnitIds;
        },
        player(state) {
            const userId = store.state.loginState.userId;

            let player = state.players.filter(playerInGame => playerInGame.user.id === userId)[0];

            if (!player) {
                player = state.players[0];
            }

            return player;
        },
        isSpectator(_, getters) {
            const userId = store.state.loginState.userId;
            return getters.player && getters.player.user.id !== userId;
        },
    },
};

export default gameState;
